import {
	Button,
	Divider,
	Drawer,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { api } from "../../../redux/api";

const NewTrackingReportModal = ({
	newTrackingStates,
	setNewTrackingStates,
	fetchTrackingReports,
	listGridAPI,
	setSnackbar
}) => {
	const drawerWidth = 450;
	const { t } = useTranslation();
	const handleBackdropClick = (event, reason) => {
		if (reason === "backdropClick") return;
	};
	const dispatch = useDispatch();
	const [reportType, setReportType] = useState("edi");
	const [reportName, setReportName] = useState("");
	const {account, user} = useSelector(state => state.queryParams);
	const [isDisabled, setIsDisabled] = useState(true);
	const [trNames, setTRNames] = useState([]);
	const [errorMessage, setErrorMessage] = useState("");
	
	// Debounced snackbar function to prevent rapid successive error messages
	const debouncedSetSnackbar = useCallback(
		debounce((snackbarConfig) => {
			setSnackbar(snackbarConfig);
		}, 300),
		[setSnackbar]
	);

	const createTrackingReport = () => {
		dispatch(api.endpoints.createTrackingReport.initiate({
			data : {
				"type": newTrackingStates.isOpenedByNewTR ? reportType : newTrackingStates?.type,
				"account_name":account,
				"user_name":user,
				"report_name":reportName,
				"fields":newTrackingStates.isOpenedByNewTR ? [] : newTrackingStates?.copyFields || []
			}
		})).then(res => {
			if (res.data?.current_state === "SUCCESS") {
				const typeForSelection = newTrackingStates.isOpenedByNewTR
					? reportType
					: (newTrackingStates?.type === "edi" ? "edi" : "other");
				fetchTrackingReports({name: reportName, type: typeForSelection});
				setSnackbar({
					isOpen:true,
					severity:'success',
					message:res.data.message
				});
				setNewTrackingStates({
					isOpen: false,
					copyFields: [],
					isOpenedByNewTR: false,
				});
			}
			else {
				debouncedSetSnackbar({
					isOpen:true,
					severity:'error',
					message: res.data?.message
				})
			}
		}).catch((err) => {
			debouncedSetSnackbar({
				isOpen:true,
				severity:'error',
				message:err?.data?.message || t("BOX_MSG_GENERAL_ERROR")
			})
		})
		
	}

	const changeHandler = (e) => {
		const {value} = e.target;
		if(trNames.includes(value)) {
			setIsDisabled(true);
			setErrorMessage(t("BOX_ERR_DUPLICATE_TR_NAME")?.replaceAll("\\n", ""))
		}
		else if (value?.length > 45) {
			setIsDisabled(true);
			setErrorMessage(t("BOX_ERR_TR_NAME_EXCEED_LIMIT"))
		}
		else if(/^[a-zA-Z0-9_]+$/.test(value)) {
			setIsDisabled(false);
		}else {
			setIsDisabled(true);
			setErrorMessage(t("BOX_ERR_INVALID_TR_NAME"))
		}
		setReportName(value)
	}

	useEffect(() => {
		if(!newTrackingStates.isOpen) {
			setReportName("");
			setReportType("edi")
		}else {
			if (listGridAPI.current) {
				const trNames = []
				listGridAPI.current.forEachNode(node => {
					trNames.push(node.data.key);
				})
				setTRNames(trNames);
			}
		}
	},[newTrackingStates])

	return (
		<Drawer
			open={newTrackingStates.isOpen}
			onClose={() => setNewTrackingStates({
				isOpen: false
			})}
			ModalProps={{
				onClose: handleBackdropClick,
			}}
			SlideProps={{
				style: {
					transform: newTrackingStates.isOpen
						? "translateX(0)"
						: "translateX(-240px)",
				},
			}}
			sx={{
				width: drawerWidth,
				flexShrink: 0,
				"& .MuiDrawer-paper": {
					width: drawerWidth,
					boxSizing: "border-box",
					boxShadow: "none",
					overflow: "hidden",
                    height: "100% !important"
				},
			}}
		>
			<div >
				<div
					className='padding-12'
					sx={{
						fontSize: "18px",
						fontWeight: "bold",
						borderBottom: "1px solid rgb(224, 224, 224)",
					}}
				>
					{t("CREATE_TRACKING_REPORT")}
				</div>
				<Divider/>
				<div
					className='padding-12'
					style={{
						marginTop: "1rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: "15px"
					}}
				>
					<div style={{display: "grid", gap: "15px"}}>
						<FormControl className='width-100' size='small'>
							<TextField autoComplete="false" autoFocus="true" label={t("NAME")} value={reportName} onChange={changeHandler} />
						</FormControl>
						{(reportName && isDisabled) ? <p className="error">{errorMessage}</p> : ""}
						{newTrackingStates?.isOpenedByNewTR  ?<FormControl className='width-100'>
							<RadioGroup
								row
								name='row-radio-buttons-group'
								value={reportType}
								onChange={(e) =>  {
									setReportType(e.target.value);
								}}
								sx={{display: "flex"}}
							>
								<FormControlLabel
									value='edi'
									control={<Radio />}
									label={t("LBL_EDI_TYPE")}
								/>
								<FormControlLabel
									value='other'
									control={<Radio />}
									label={t("LBL_OTHER_TYPE")}
								/>
							</RadioGroup>
						</FormControl> : ""}
					</div>
					<div style={{display: "flex", justifyContent: "space-between", gap: "10px", marginTop: "20px"}}>
                        <div></div>
                        <div style={{display: "flex", justifyContent: "space-between", gap: "10px"}}>
                            <Button variant='outlined' disabled={isDisabled} onClick={createTrackingReport} >{t("BTN_OK")}</Button>
                            <Button variant='outlined' onClick={() => setNewTrackingStates({
								isOpen: false
							})}>{t("CANCEL")}</Button>
                        </div>
					</div>
				</div>
			</div>
		</Drawer>
	);
};

export default NewTrackingReportModal;
